import { JaneGoldUserCallout } from '@iheartjane/menu';
import { Box, Flex, JaneGoldIcon, Typography } from '@iheartjane/reefer';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';

import type { RootLoaderType } from '../../../data-access';
import {
  useKioskJaneGoldData,
  useKioskSpacing,
  useTypedRouteLoaderData,
} from '../../../hooks';
import { trackLinkBankAccountTap, trackSignInTap } from '../../../util';
import { LearnMoreLink } from '../LearnMoreLink/LearnMoreLink';
import { JaneGoldBundleProgressCalloutContainer } from './JaneGoldBundleProgressCallout/JaneGoldBundleProgressCallout';
import type {
  JaneGoldCalloutCartVariantProps,
  JaneGoldCalloutProps,
} from './JaneGoldCallout.types';
import { JaneGoldCartCallout } from './JaneGoldCartCallout/JaneGoldCartCallout';
import { JaneGoldPdpCalloutContainer } from './JaneGoldPdpCallout/JaneGoldPdpCallout';

export const JaneGoldCallout = ({
  variant,
  ...props
}: JaneGoldCalloutProps) => {
  const { store } = useTypedRouteLoaderData<RootLoaderType>('root');
  const navigate = useNavigate();
  const { brandSpecialId } = useParams<{ brandSpecialId?: string }>();

  const { userSignedIn, userHasLinkedBank } = useKioskJaneGoldData();
  const { getKioskSpacing, isIpad } = useKioskSpacing();

  const showSignIn =
    store?.kiosk_anonymous_checkout !== 'required' && store?.kiosk_allow_auth;

  const handleLoginClick = useCallback(
    (source: string, url: string) => {
      trackSignInTap({
        store,
        source: [source],
      });
      navigate(`/stores/${store.id}/${url}/login`);
    },
    [navigate, store]
  );

  const handleLinkBankClick = useCallback(
    (source: string, url: string) => {
      trackLinkBankAccountTap({
        deviceType: 'Kiosk',
        source: [source],
        store,
      });
      navigate(`/stores/${store.id}/${url}/link-bank`);
    },
    [navigate, store]
  );

  if (!showSignIn) {
    return null;
  }

  return (
    <>
      {variant === 'bundleProgress' && 'bundlesInProgress' in props && (
        <JaneGoldBundleProgressCalloutContainer
          bundlesInProgress={props.bundlesInProgress}
          cart={props.cart}
          onClick={props.onClick}
          userSignedIn={userSignedIn}
        />
      )}

      {variant === 'cart' && (
        <JaneGoldCartCallout
          {...(props as JaneGoldCalloutCartVariantProps)}
          onLinkBankClick={() => handleLinkBankClick('Cart', 'cart')}
          onLoginClick={() => handleLoginClick('Cart', 'cart')}
          userSignedIn={userSignedIn}
          userHasLinkedBank={userHasLinkedBank}
        />
      )}

      {variant === 'pdp' && 'brandSpecials' in props && (
        <Box mb={24}>
          <Flex flexDirection="row" alignContent="center" gap={8}>
            <JaneGoldIcon size="md" />
            <Typography mb={getKioskSpacing(24, 16)} variant="body-bold">
              Jane Gold
            </Typography>
          </Flex>
          <Flex flexDirection="column" gap={16}>
            {props.brandSpecials.map((brandSpecial) => (
              <JaneGoldPdpCalloutContainer
                key={brandSpecial.id}
                brandSpecial={brandSpecial}
                onClick={() => props.onClick?.(brandSpecial)}
                userSignedIn={userSignedIn}
                storeId={store.id}
                {...props}
              />
            ))}
          </Flex>
        </Box>
      )}

      {variant === 'user' && 'storeState' in props && (
        <JaneGoldUserCallout
          branded
          borderRadius={isIpad ? 'lg' : 'sm'}
          boxShadow={!props.scrolledFromTop}
          buttonVariant="primary-inverse"
          disableMobileStyling
          iconSize={isIpad ? 'md' : 'lg'}
          learnMoreLink={
            <LearnMoreLink userHasLinkedBank={userHasLinkedBank} />
          }
          onLinkAccountClick={() => {
            const { source, url } = brandSpecialId
              ? {
                  source: 'Brand Special',
                  url: `menu/jane-gold/${brandSpecialId}`,
                }
              : { source: 'Menu', url: 'menu/jane-gold' };
            userSignedIn
              ? handleLinkBankClick(source, url)
              : handleLoginClick(source, url);
          }}
          showDefaultLearnMoreLink={false}
          storeState={props.storeState}
          userSignedIn={userSignedIn}
          userHasLinkedBank={userHasLinkedBank}
          p={isIpad ? 16 : 24}
        />
      )}
    </>
  );
};
